import { useBottomSheet } from '../../../../../../state-management/contexts/BottomSheetContext'
import { useToast } from '../../../../../../state-management/contexts/ToastContext'
import type { SipDecreaseRecommendation } from '../../../sip/api/sipPlan.types'
import { postFilledSipPlanCta } from '../../fundExecutionHelpers'
import { formatRupeeLabel } from '../../../display-values/lumpsumPlanAmount'
import { formatTargetSip } from '../../../display-values/sipPlanAmount'
import {
  FolioSourceId,
  SipFolioActionTone,
  type SipFolioRow,
  sumFolioSipAmounts
} from '../folio'
import { SipFolioRowList, SipModifySheetLayout } from '../ModifySheet'
import { EventNames } from '../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../utils/appBridge'
import { PlansExecutionEventIds, PlansScreen } from '../../../analytics/plansEvents'

const COPY = {
  externalHelper: 'Go to your broker app where it was originally set up and do the updates',
  infiniteHelper: 'Contact your circle manager for more information',
  noChangeNeeded: 'No change needed'
} as const

export function useDecreaseSipAction(options?: { isRecommendationSheetSource?: boolean }) {
  const {
    openBottomSheet,
    closeBottomSheet,
    openNestedBottomSheet,
    closeNestedBottomSheet
  } = useBottomSheet()
  const openSheet = options?.isRecommendationSheetSource ? openNestedBottomSheet : openBottomSheet
  const closeSheet = options?.isRecommendationSheetSource ? closeNestedBottomSheet : closeBottomSheet
  const { showToast } = useToast()

  function onDecreaseSip(recommendation: SipDecreaseRecommendation, manageSipCta: unknown) {
    const fundName = recommendation?.fundName ?? ''
    const fundIsin = recommendation?.isin ?? ''
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: PlansScreen.SipYourPlanRecommendation,
        eventId: PlansExecutionEventIds.SipDecrease,
        buttonLabel: 'Decrease SIP',
        ...(fundName ? { fundName } : {}),
        ...(fundIsin ? { fundIsin } : {})
      }
    })
    openSheet(
      <DecreaseSipModifyBottomSheet
        onDismiss={closeSheet}
        recommendation={recommendation}
        onPowerUpClick={() => {
          postFilledSipPlanCta(manageSipCta, { isin: fundIsin }, showToast)
        }}
      />,
      closeSheet
    )
  }

  return { onDecreaseSip }
}

export function DecreaseSipModifyBottomSheet({
  onDismiss,
  recommendation,
  onPowerUpClick
}: {
  onDismiss: () => void
  recommendation: SipDecreaseRecommendation
  onPowerUpClick?: () => void
}) {
  return (
    <SipModifySheetLayout
      onDismiss={onDismiss}
      mfLogoUrl={recommendation?.mfLogoUrl ?? ''}
      fundName={recommendation?.fundName ?? ''}
      fundIsin={recommendation?.isin ?? ''}
      heading={`Reduce total SIP to ${formatTargetSip({ rawAmount: recommendation?.recommendedSipAmount ?? 0 }).label}/month`}
    >
      <SipFolioRowList
        rows={buildDecreaseFolioRows(recommendation)}
        fundName={recommendation?.fundName ?? ''}
        fundIsin={recommendation?.isin ?? ''}
        onPowerUpClick={onPowerUpClick}
      />
    </SipModifySheetLayout>
  )
}

type RemainingFolioSipAmounts = {
  powerupSipAmount: number
  externalSipAmount: number
  infiniteSipAmount: number
}

/**
 * Product reduce order: External first (not ours) → PowerUp → Infinite last (highest revenue).
 * Returns how much SIP should remain on each source (backend keys).
 */
function resolveRemainingAmounts(recommendation: SipDecreaseRecommendation): RemainingFolioSipAmounts {
  let amountToRemove = Math.max(
    0,
    sumFolioSipAmounts(recommendation) - (recommendation?.recommendedSipAmount ?? 0)
  )

  let externalSipAmount = recommendation?.externalSipAmount ?? 0
  let powerupSipAmount = recommendation?.powerupSipAmount ?? 0
  let infiniteSipAmount = recommendation?.infiniteSipAmount ?? 0

  const removeFromExternal = Math.min(externalSipAmount, amountToRemove)
  externalSipAmount -= removeFromExternal
  amountToRemove -= removeFromExternal

  const removeFromPowerup = Math.min(powerupSipAmount, amountToRemove)
  powerupSipAmount -= removeFromPowerup
  amountToRemove -= removeFromPowerup

  const removeFromInfinite = Math.min(infiniteSipAmount, amountToRemove)
  infiniteSipAmount -= removeFromInfinite

  return { powerupSipAmount, externalSipAmount, infiniteSipAmount }
}

/**
 * Product display order: External → PowerUp → Infinite.
 * Action copy depends on current vs remaining after resolveRemainingAmounts.
 */
function buildDecreaseFolioRows(recommendation: SipDecreaseRecommendation): SipFolioRow[] {
  const remaining = resolveRemainingAmounts(recommendation)
  const rows: SipFolioRow[] = []
  const externalSipAmount = recommendation?.externalSipAmount ?? 0
  const powerupSipAmount = recommendation?.powerupSipAmount ?? 0
  const infiniteSipAmount = recommendation?.infiniteSipAmount ?? 0

  if (externalSipAmount > 0) {
    rows.push(
      buildDecreaseExternalRow(externalSipAmount, remaining.externalSipAmount)
    )
  }

  if (powerupSipAmount > 0) {
    rows.push(buildDecreasePowerUpRow(powerupSipAmount, remaining.powerupSipAmount))
  }

  if (infiniteSipAmount > 0) {
    rows.push(
      buildDecreaseInfiniteRow(infiniteSipAmount, remaining.infiniteSipAmount)
    )
  }

  return rows
}

function buildDecreasePowerUpRow(
  powerupSipAmount: number,
  remainingAmount: number
): SipFolioRow {
  // This folio was not touched by the reduce plan.
  if (remainingAmount === powerupSipAmount) {
    return {
      id: FolioSourceId.POWERUP,
      currentAmount: powerupSipAmount,
      actionText: COPY.noChangeNeeded,
      actionTone: SipFolioActionTone.NEUTRAL,
      helperText: null,
      interactive: false
    }
  }

  // Fully drained → stop (manage CTA; no cancel/restart steps).
  if (remainingAmount <= 0) {
    return {
      id: FolioSourceId.POWERUP,
      currentAmount: powerupSipAmount,
      actionText: `Stop this SIP of ${formatRupeeLabel({ rawAmount: powerupSipAmount })}/mo`,
      actionTone: SipFolioActionTone.DANGER,
      helperText: null,
      interactive: true
    }
  }

  // Partial reduce: editing isn't supported yet → cancel + restart steps.
  return {
    id: FolioSourceId.POWERUP,
    currentAmount: powerupSipAmount,
    actionText: `Reduce SIP to ${formatRupeeLabel({ rawAmount: remainingAmount })}/mo`,
    actionTone: SipFolioActionTone.DANGER,
    helperText: null,
    powerUpRestartSteps: { currentAmount: powerupSipAmount, remainingAmount },
    interactive: true
  }
}

function buildDecreaseExternalRow(
  externalSipAmount: number,
  remainingAmount: number
): SipFolioRow {
  return buildDecreaseOffPlatformRow({
    sourceId: FolioSourceId.EXTERNAL,
    currentAmount: externalSipAmount,
    remainingAmount,
    helperText: COPY.externalHelper,
    // Product copy differs slightly from Infinite / PowerUp.
    reduceActionText: `Reduce this SIP to ${formatRupeeLabel({ rawAmount: remainingAmount })}/mo`
  })
}

function buildDecreaseInfiniteRow(
  infiniteSipAmount: number,
  remainingAmount: number
): SipFolioRow {
  return buildDecreaseOffPlatformRow({
    sourceId: FolioSourceId.INFINITE,
    currentAmount: infiniteSipAmount,
    remainingAmount,
    helperText: COPY.infiniteHelper,
    reduceActionText: `Reduce SIP to ${formatRupeeLabel({ rawAmount: remainingAmount })}/mo`
  })
}

/** External / Infinite: same stop / reduce / no-change branches; not tappable. */
function buildDecreaseOffPlatformRow({
  sourceId,
  currentAmount,
  remainingAmount,
  helperText,
  reduceActionText
}: {
  sourceId: FolioSourceId.EXTERNAL | FolioSourceId.INFINITE
  currentAmount: number
  remainingAmount: number
  helperText: string
  reduceActionText: string
}): SipFolioRow {
  if (remainingAmount <= 0) {
    return {
      id: sourceId,
      currentAmount,
      actionText: `Stop this SIP of ${formatRupeeLabel({ rawAmount: currentAmount })}/mo`,
      actionTone: SipFolioActionTone.DANGER,
      helperText,
      interactive: false
    }
  }

  if (remainingAmount < currentAmount) {
    return {
      id: sourceId,
      currentAmount,
      actionText: reduceActionText,
      actionTone: SipFolioActionTone.DANGER,
      helperText,
      interactive: false
    }
  }

  return {
    id: sourceId,
    currentAmount,
    actionText: COPY.noChangeNeeded,
    actionTone: SipFolioActionTone.NEUTRAL,
    helperText: null,
    interactive: false
  }
}
